import { Clock, Heart } from "react-feather";
import { useEffect, useState } from "react";
import { fundraiserService } from "../services/api";
import Header from "../components/header";
import Footer from "../components/footer";
import FloatingChatButton from "../components/FloatingChatButton";

const AllFundraisers = () => {
  const [fundraisers, setFundraisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  const fetchFundraisers = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: 20,
        status: 'active',
        sortBy,
        ...(search && { search })
      };
      
      const response = await fundraiserService.getAll(params);
      setFundraisers(response.data.fundraisers || []);
      setTotalPages(response.data.pagination?.total || 1);
    } catch (error) {
      console.error('Error fetching fundraisers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDonate = (fundraiser) => {
    const message = `Hi, I would like to donate to the fundraiser: "${fundraiser.title}" for ${fundraiser.patientName}. Please provide me with the donation details.`;
    const encodedMessage = encodeURIComponent(message);
    const whatsappNumber = fundraiser.whatsappNumber.replace(/\D/g, ''); // Remove non-digits
    const whatsappUrl = `https://wa.me/${9599222655}?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  useEffect(() => {
    fetchFundraisers();
  }, [currentPage, search, sortBy]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <>
      <Header />
      <section className="bg-gray-50 min-h-screen py-16 px-4 sm:px-8 lg:px-16">
      <h2 className="text-3xl md:text-4xl font-bold text-center text-gray-800 mb-8">
        All Active Fundraisers
      </h2>

      {/* Search and Filter */}
      <div className="max-w-4xl mx-auto mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <input
              type="text"
              placeholder="Search fundraisers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <div>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="newest">Newest First</option>
              <option value="amount_high">Highest Amount</option>
              <option value="amount_low">Lowest Amount</option>
              <option value="progress">Most Progress</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading fundraisers...</p>
        </div>
      ) : fundraisers.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-gray-600">No fundraisers found.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-8">
            {fundraisers.map((f) => (
              <div
                key={f._id}
                className="bg-white rounded-xl shadow-md overflow-hidden flex flex-col justify-between cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => window.open(`/fundraiser/${f._id}`, '_blank')}
              >
                <img
                  src={f.mainImage}
                  alt="Fundraiser banner"
                  className="w-full h-40 object-cover"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/600x300?text=Fundraiser+Banner';
                  }}
                />

                <div className="p-4">
                  <h3 className="text-sm font-semibold text-gray-800 leading-tight mb-2 line-clamp-2">
                    {f.title}
                  </h3>

                  <p className="text-xs text-gray-600 mb-2">
                    by <span className="font-semibold">{f.patientName}</span>
                  </p>

                  <p className="text-sm font-bold text-gray-800 mb-1">
                    {f.formattedRaisedAmount}{" "}
                    <span className="font-normal text-gray-500">
                      raised out of {f.formattedGoalAmount}
                    </span>
                  </p>

                  <div className="w-full h-2 bg-blue-100 rounded-full my-2">
                    <div 
                      className="h-full bg-blue-400 rounded-full" 
                      style={{ width: `${Math.min(f.progressPercentage || 0, 100)}%` }}
                    ></div>
                  </div>

                  <p className="text-xs text-gray-500">{f.lastContributionFormatted}</p>

                  <div className="flex justify-between text-xs text-gray-500 mt-4">
                    <span className="flex items-center gap-1">
                      <Clock size={14} /> {f.daysLeft ? `${f.daysLeft} Days Left` : 'No deadline'}
                    </span>
                    <span className="flex items-center gap-1">
                      <Heart size={14} className="text-red-500" /> {f.supporters}{" "}
                      Supporters
                    </span>
                  </div>
                </div>

                <div className="p-4 pt-2">
                  <button 
                    className="w-full bg-blue-600 text-white py-2 rounded-lg font-semibold hover:bg-blue-700 transition"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDonate(f);
                    }}
                  >
                    Donate Now
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-center mt-12">
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Previous
                </button>
                
                {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                  const page = Math.max(1, currentPage - 2) + index;
                  if (page > totalPages) return null;
                  
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-4 py-2 border rounded-md ${
                        currentPage === page
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
    <Footer />
    <FloatingChatButton />
    </>
  );
};

export default AllFundraisers;
